/**
 * Polygon-selection drawing tool.
 *
 * A pure drawing tool: places a polygon in the map and, on close, hands off
 * to area-mode (`areaMode.ts`) which owns the persistent UI state — the polygon
 * entity itself and the masts found inside it. (Section info and load cases
 * belong to path-mode, entered through the `mastpointsAlongLine` tool.)
 *
 * Two input states:
 *   1. Drawing — LEFT_CLICK adds vertex, RIGHT_CLICK closes (≥3 vertices),
 *      MOUSE_MOVE tracks preview line.
 *   2. Idle — no handlers; either we haven't started, or we've handed off.
 *
 * Tool lifecycle is the standard ToolProperties pattern: another tool's
 * `disableAllToolsExceptToggleToolByName` flips `polygonSelection.toolEnabled`
 * off, the subscribe below tears down. The polygon entity, if drawn, is owned
 * by area-mode at that point and stays in the viewer.
 */

import {
	Cartographic,
	Color,
	ColorMaterialProperty,
	defined,
	HeightReference,
	PolygonHierarchy,
	ScreenSpaceEventType,
	CallbackProperty,
	type Cartesian3,
	type Entity,
	type Viewer,
    Cartesian2
} from 'cesium';
import { get } from 'svelte/store';

import { useCesiumViewer } from '../cesium-viewer';
import { useDataSources } from '../cesium-datasources';
import { mapModeStore } from '../map-mode-store';
import { mapToolsStore } from '../map-tools-store';
import { OriginalCesiumInputActions } from '../cesium-original-input-actions';
import { getPolygonBoundariesAsLatLngs } from '../map-utils';
import { toggleEscapeToolListener } from '../map-tools';
import { ENTITY_ID_PREFIX } from '$lib/utils/constants';
import { activateAreaMode } from './areaMode';
import { v4 } from 'uuid';

// ── Types ────────────────────────────────────────────────────────────────────

type SelectableEntityType = 'point' | 'cylinder' | 'polyline' | 'box' | 'mastpoint';

const PREFIX_TYPE_MAP: Record<string, SelectableEntityType> = {
	[ENTITY_ID_PREFIX.MASTPOINT]: 'mastpoint',
	[ENTITY_ID_PREFIX.POLE]: 'cylinder',
	[ENTITY_ID_PREFIX.LINE]: 'polyline',
	[ENTITY_ID_PREFIX.CROSSBEAM]: 'box',
	[ENTITY_ID_PREFIX.SUPPORT]: 'cylinder'
};

function resolveEntityType(entityId: string): SelectableEntityType | null {
	for (const [prefix, type] of Object.entries(PREFIX_TYPE_MAP)) {
		if (entityId.startsWith(prefix)) return type;
	}
	return null;
}

// ── Module-local state ───────────────────────────────────────────────────────

type ToolState = 'idle' | 'drawing';

/**
 * Reuse options: the drawing UX (vertices, preview, right-click close) is
 * shared, but a caller can flag a different map-tools-store key and take over
 * what happens with the finished ring instead of the default area-mode hand-off.
 */
export type PolygonDrawingOptions = {
	/** Store key flipped while drawing (default `polygonSelection`). */
	toolKey?: Extract<import('../map-tools-store').ToolPropertyKey, 'polygonSelection' | 'imageAreaSelection'>;
	/**
	 * Custom completion. Receives the drawn vertices and the CLOSED `[lng, lat]`
	 * ring (GeoJSON order, first point repeated last). When set, the default
	 * `polygonArea` entity + area-mode hand-off is skipped — the callback owns
	 * the shape from here.
	 */
	onComplete?: (result: {
		viewer: Viewer;
		vertices: Cartesian3[];
		/** Open `[lng, lat]` boundary (no closing duplicate). */
		polygonLngLat: [number, number][];
		/** Closed `[lng, lat]` ring, ready for a GeoJSON Polygon. */
		ring: [number, number][];
	}) => void;
};

let state: ToolState = 'idle';
let activeToolKey: NonNullable<PolygonDrawingOptions['toolKey']> = 'polygonSelection';
let customOnComplete: PolygonDrawingOptions['onComplete'] = undefined;
let vertices: Cartesian3[] = [];
let polylineEntity: Entity | null = null;
let previewPolylineEntity: Entity | null = null;
let vertexHandleEntities: Entity[] = [];
let mouseCartesian: Cartesian3 | null = null;

let leftClickHandler: ((e: { position: { x: number; y: number } }) => void) | null = null;
let rightClickHandler: ((e: { position: { x: number; y: number } }) => void) | null = null;
let mouseMoveHandler: ((e: { endPosition: { x: number; y: number } }) => void) | null = null;

// ── Shared-state subscriptions ──────────────────────────────────────────────
// React when another tool (or disableAllTools) flips toolEnabled off so we
// can tear our drawing handlers down. The polygon entity, if any, is owned
// by area-mode by then and stays.

// Installed lazily on first activation, NOT at module scope: this module sits
// on a circular import chain (map-tools-store → map-utils → map-event-listeners
// → selected-entity-behaviour → imageAreaTool → here), so touching
// `mapToolsStore` during module evaluation throws a TDZ ReferenceError whenever
// the cycle happens to be entered from the store's side. Before the first
// activation `state` is 'idle' and the subscription would be a no-op anyway.
let teardownSubscriptionInstalled = false;
function installTeardownSubscription(): void {
	if (teardownSubscriptionInstalled) return;
	teardownSubscriptionInstalled = true;
	mapToolsStore.subscribe((store) => {
		if (!store[activeToolKey].toolEnabled && state !== 'idle') {
			teardown();
		}
	});
}

// ── Public API ───────────────────────────────────────────────────────────────

export function activatePolygonSelectionTool(options?: PolygonDrawingOptions): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;

	installTeardownSubscription();

	activeToolKey = options?.toolKey ?? 'polygonSelection';
	customOnComplete = options?.onComplete;

	// Disable any other active tool first; this also restores Cesium's
	// default LEFT_CLICK and removes lingering input actions.
	mapToolsStore.disableAllTools(viewer);

	// scene.pickPosition needs depth-testing against terrain to return
	// accurate world positions when a custom terrain provider is active.
	viewer.scene.globe.depthTestAgainstTerrain = true;
	vertices = [];
	mouseCartesian = null;

	installDrawingHandlers(viewer);
	createPreviewPolygon(viewer);
	state = 'drawing';

	mapToolsStore.update((store) => {
		store[activeToolKey].toolEnabled = true;
		// Esc cancels the drawing-in-progress and tears the tool down.
		store[activeToolKey].deactivateTool = cancelDrawing;
		return store;
	});

	toggleEscapeToolListener({ toggleOn: true });
}

/** Cancel drawing — used by Esc when the polygon hasn't been finalised yet. */
export function cancelDrawing(): void {
	const viewer = useCesiumViewer();
	if (!viewer) return;
	mapToolsStore.disableAllTools(viewer);
	toggleEscapeToolListener({ toggleOn: false });
}

// ── Internal: handler installation ──────────────────────────────────────────

function installDrawingHandlers(viewer: Viewer): void {
	const handler = viewer.screenSpaceEventHandler;

	leftClickHandler = (event) => {
		const cartesian = pickPosition(viewer, event.position);
		if (!cartesian) return;
		vertices = [...vertices, cartesian];
		syncVertexHandles(viewer);
		updatePreviewPolyline(viewer);
	};

	rightClickHandler = () => {
		if (vertices.length < 3) return;
		finalisePolygon(viewer);
	};

	mouseMoveHandler = (event) => {
		mouseCartesian = pickPosition(viewer, event.endPosition);
	};

	handler.setInputAction(leftClickHandler, ScreenSpaceEventType.LEFT_CLICK);
	handler.setInputAction(rightClickHandler, ScreenSpaceEventType.RIGHT_CLICK);
	handler.setInputAction(mouseMoveHandler, ScreenSpaceEventType.MOUSE_MOVE);
}

function teardown(): void {
	if (state === 'idle') return;
	const viewer = useCesiumViewer();
	if (!viewer) return;

	const handler = viewer.screenSpaceEventHandler;
	handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
	handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
	handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);

	if (OriginalCesiumInputActions.LEFT_CLICK) {
		handler.setInputAction(
			OriginalCesiumInputActions.LEFT_CLICK,
			ScreenSpaceEventType.LEFT_CLICK
		);
	}

	leftClickHandler = null;
	rightClickHandler = null;
	mouseMoveHandler = null;

	removePreviewPolyline(viewer);
	removePreviewPolygon(viewer);
	removeVertexHandles(viewer);
	vertices = [];
	mouseCartesian = null;
	customOnComplete = undefined;
	state = 'idle';
}

// ── Internal: position picking ──────────────────────────────────────────────

function pickPosition(
	viewer: Viewer,
	windowPosition: { x: number; y: number }
): Cartesian3 | null {
    const windowCartesian = new Cartesian2(windowPosition.x, windowPosition.y);
	const fromScene = viewer.scene.pickPosition(windowCartesian);
	if (defined(fromScene)) return fromScene;
	return viewer.camera.pickEllipsoid(windowCartesian, viewer.scene.globe.ellipsoid) ?? null;
}

// ── Internal: preview drawing ───────────────────────────────────────────────

function updatePreviewPolyline(viewer: Viewer): void {
	removePreviewPolyline(viewer);
	if (vertices.length < 2) return;
	const positions: Cartesian3[] = [...vertices, vertices[0]];
	polylineEntity = viewer.entities.add({
		polyline: {
			positions,
			width: 2,
			material: new ColorMaterialProperty(Color.WHITE.withAlpha(0.7)),
			clampToGround: true
		}
	});
}

function removePreviewPolyline(viewer: Viewer): void {
	if (polylineEntity) {
		viewer.entities.remove(polylineEntity);
		polylineEntity = null;
	}
}

function createPreviewPolygon(viewer: Viewer): void {
	previewPolylineEntity = viewer.entities.add({
		polyline: {
			positions: new CallbackProperty(() => {
				if (vertices.length < 1 || !mouseCartesian) return [];
				return [...vertices, mouseCartesian, vertices[0]];
			}, false),
			width: 2,
			material: new ColorMaterialProperty(Color.YELLOW.withAlpha(0.8)),
			clampToGround: true
		}
	});
}

function removePreviewPolygon(viewer: Viewer): void {
	if (previewPolylineEntity) {
		viewer.entities.remove(previewPolylineEntity);
		previewPolylineEntity = null;
	}
}

function syncVertexHandles(viewer: Viewer): void {
	removeVertexHandles(viewer);
	vertexHandleEntities = vertices.map((position) =>
		viewer.entities.add({
			position,
			point: {
				pixelSize: 8,
				color: Color.WHITE,
				outlineColor: Color.CYAN,
				outlineWidth: 2,
				disableDepthTestDistance: Number.POSITIVE_INFINITY
			}
		})
	);
}

function removeVertexHandles(viewer: Viewer): void {
	vertexHandleEntities.forEach((e) => viewer.entities.remove(e));
	vertexHandleEntities = [];
}

// ── Internal: finalisation ──────────────────────────────────────────────────

function finalisePolygon(viewer: Viewer): void {
	removePreviewPolyline(viewer);
	removePreviewPolygon(viewer);
	removeVertexHandles(viewer);

	const polygonVertices = [...vertices];

	// Custom completion (e.g. the image-area tool): hand over the ring and skip
	// the default polygonArea entity + area-mode hand-off entirely.
	if (customOnComplete) {
		const boundary = getPolygonBoundariesAsLatLngs(polygonVertices);
		const closed: [number, number][] = [...boundary];
		if (
			closed.length >= 3 &&
			(closed[0][0] !== closed[closed.length - 1][0] ||
				closed[0][1] !== closed[closed.length - 1][1])
		) {
			closed.push(closed[0]);
		}
		const onComplete = customOnComplete;
		toggleEscapeToolListener({ toggleOn: false });
		onComplete({ viewer, vertices: polygonVertices, polygonLngLat: boundary, ring: closed });
		// Flip the tool flag off — the subscribe tears the drawing handlers down.
		mapToolsStore.update((store) => {
			store[activeToolKey].toolEnabled = false;
			return store;
		});
		state = 'idle';
		return;
	}

	const polygonEntity = viewer.entities.add({
        id: `polygonArea:${v4()}`,
		polygon: {
			hierarchy: new PolygonHierarchy(polygonVertices),
			material: Color.CYAN.withAlpha(0.15),
			outline: true,
			outlineColor: Color.CYAN,
			outlineWidth: 2,
			heightReference: HeightReference.CLAMP_TO_GROUND
		}
	});

	const found = detectEntitiesInPolygon(viewer, polygonVertices, polygonEntity);
	const mastpoints = found.filter((entity) => entity.id.startsWith(ENTITY_ID_PREFIX.MASTPOINT));

	const polygonLngLat = getPolygonBoundariesAsLatLngs(polygonVertices);
	const ring: [number, number][] = [...polygonLngLat];
	if (
		ring.length >= 3 &&
		(ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])
	) {
		ring.push(ring[0]);
	}

	// Detach Esc handler we registered on activation; area-mode installs its
	// own. Tool teardown happens via the subscribe when toolEnabled flips.
	toggleEscapeToolListener({ toggleOn: false });

	activateAreaMode({
		polygonEntity,
		mastpoints,
		polygonLngLat,
		polygon: { type: 'Polygon', coordinates: [ring] }
	});

	vertices = [];
	state = 'idle';
	// Drawing-tool flag is flipped off inside activateAreaMode, which triggers
	// teardown via the subscribe — so handlers come down without us doing it
	// here.
}

// ── Internal: entity hit-test ───────────────────────────────────────────────

function detectEntitiesInPolygon(
	viewer: Viewer,
	polygonVertices: Cartesian3[],
	polygonEntity: Entity
): Entity[] {
	const { mapdataDataSource } = useDataSources();
	const planDataSource = get(mapModeStore).activeDataSource;
	const sources = [mapdataDataSource, planDataSource];
	const found: Entity[] = [];

	sources.forEach((source) => {
		source?.entities.values.forEach((entity) => {
			if (entity === polygonEntity || entity === polylineEntity) return;
			if (!resolveEntityType(entity.id)) return;
			if (!entity.show) return;
			const position = getEntityPosition(viewer, entity);
			if (!position) return;
			const carto = Cartographic.fromCartesian(position);
			if (pointInPolygon(carto, polygonVertices)) found.push(entity);
		});
	});

	return found;
}

function getEntityPosition(viewer: Viewer, entity: Entity): Cartesian3 | null {
	const time = viewer.clock.currentTime;
	if (entity.position) return entity.position.getValue(time) ?? null;
	if (entity.polygon?.hierarchy) {
		const hierarchy = entity.polygon.hierarchy.getValue(time) as
			| { positions: Cartesian3[] }
			| undefined;
		if (hierarchy?.positions?.length) return hierarchy.positions[0];
	}
	if (entity.polyline?.positions) {
		const positions = entity.polyline.positions.getValue(time) as Cartesian3[] | undefined;
		if (positions?.length) return positions[0];
	}
	return null;
}

/** Ray-casting point-in-polygon test in cartographic (lon/lat) space. */
function pointInPolygon(pointCarto: Cartographic, polygonCartesian: Cartesian3[]): boolean {
	const poly = polygonCartesian.map((c) => Cartographic.fromCartesian(c));
	const px = pointCarto.longitude;
	const py = pointCarto.latitude;
	let inside = false;
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		const xi = poly[i].longitude;
		const yi = poly[i].latitude;
		const xj = poly[j].longitude;
		const yj = poly[j].latitude;
		const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
		if (intersect) inside = !inside;
	}
	return inside;
}
